/**
 * Which part of the map the detail panel is sitting on.
 *
 * The panel is a column beside the map on a wide screen and a sheet across the
 * bottom on a narrow one. Assuming the first, the map was told to keep the
 * panel's width clear on its right — which on a phone is the entire width, so
 * framing a region asked for more padding than the map had and nothing moved at
 * all. It also judged "in view" from the left quarter of a map whose right-hand
 * side was perfectly visible.
 *
 * So the side is measured rather than assumed, and the same code serves both
 * layouts without asking how wide the window is.
 */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Obscured {
  /** Pixels covered along the right edge. */
  right: number;
  /** Pixels covered along the bottom edge. */
  bottom: number;
}

const NONE: Obscured = { right: 0, bottom: 0 };

export function obscuredBy(map: Rect, panel: Rect): Obscured {
  const width = map.right - map.left;
  const height = map.bottom - map.top;
  if (width <= 0 || height <= 0) return NONE;

  // A panel that is not on screen, or not over the map, covers nothing.
  if (panel.right <= panel.left || panel.bottom <= panel.top) return NONE;
  if (panel.left >= map.right || panel.right <= map.left) return NONE;
  if (panel.top >= map.bottom || panel.bottom <= map.top) return NONE;

  // Spanning the map's full width means it can only be taking the bottom from
  // it; a sheet pinned to the bottom of the screen is the case in hand.
  const spansWidth = panel.left <= map.left + 1 && panel.right >= map.right - 1;
  if (spansWidth) {
    return { right: 0, bottom: Math.min(height, map.bottom - Math.max(panel.top, map.top)) };
  }

  return { right: Math.min(width, map.right - Math.max(panel.left, map.left)), bottom: 0 };
}
